import clsx from 'clsx'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchContext } from '../contexts/search_context'
import { FilterPriceRange, Filters } from '../types'
import { useForm, useWatch } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { hrefWithSearch, isAvailableOnly, isCategoryBrowse, withSortFrom } from '../lib/browse_params'

import { games } from '../lib/games'
import { views } from '../lib/views'
import { cardTypes } from '../lib/card_types'
import { energyTypes } from '../lib/energy_types'
import { rarities } from '../lib/rarities'
import { stocks } from '../lib/stocks'

const conditions: number[] = [90, 70, 50, 20, 10, 0]

type MenuHeaderProps = Readonly<{
  label: string
  menuTree: string[]
  depth: number
  backCallback: (string, number) => void
}>

const MenuHeader = ({ label, menuTree, depth, backCallback }: MenuHeaderProps) => {
  return <li className='header'>
    <span className='back' onClick={ _ => { backCallback(menuTree, depth - 1) } }>←</span>
    <span className='title'>{ label }</span>
  </li>
}

type MenuItemProps = Readonly<{
  id: string|number
  ordinality?: number
  label: string
  selected: boolean
  callback?: (number, boolean) => void
}>

const MenuItem = ({ id, ordinality, label, selected, callback }: MenuItemProps) => {
  return <li style={ { order: ordinality } } className={ clsx({ selected }) } onClick={ _ => { callback?.(id, !selected) } }>
    <span className='checkbox'></span>
    <span className='option'>{ label }</span>
  </li>
}

type SelectAllProps = Readonly<{
  selectCallback: () => void
}>

const SelectAll = ({ selectCallback }: SelectAllProps) => {
  const { t } = useTranslation()

  return <li className='selectall' onClick={ selectCallback }>
    <span>{ t('cards.filter_menu.select_all') }</span>
  </li>
}

type SubviewProps = Readonly<{
  filters: Filters
  setChangedFiltersCallback: (filters: Filters) => void
  toggleViewCallback: (arg0: string[], arg1: number) => void
  submenu: string[]
}>


const GameMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const { t } = useTranslation()

  const toggleView = useCallback((e) => {
    const newFilters: Filters = {
      ...filters,
      selectedGame: e,
    }

    setChangedFiltersCallback(newFilters)
  }, [filters, setChangedFiltersCallback])

  return <ul className='filter-menu menu-sub'>
    <MenuHeader label={ t('cards.game.title') } menuTree= { ['main'] } depth={ 1 } backCallback={ toggleViewCallback } />
    {
      games.map((e) => <MenuItem
        key={ e.id }
        id={ e.id }
        label={ t(`cards.game.${e.name}`) }
        selected={ e.id === filters.selectedGame }
        callback={ toggleView }
      />)
    }
  </ul>
}

const ViewMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const { t } = useTranslation()

  const toggleView = useCallback((e) => {
    const newFilters: Filters = {
      ...filters,
      selectedView: e,
    }

    setChangedFiltersCallback(newFilters)
  }, [filters, setChangedFiltersCallback])

  return <ul className='filter-menu menu-sub'>
    <MenuHeader label={ t('card_views.title') } menuTree= { ['main'] } depth={ 1 } backCallback={ toggleViewCallback } />
    {
      views.map((e) => <MenuItem
        key={ e }
        id={ e }
        label={ t(`card_views.${e}`) }
        selected={ e === filters.selectedView }
        callback={ toggleView }
      />)
    }
  </ul>
}

const StockMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const { t } = useTranslation()
  const selection = filters.selectedStocks

  const toggleOption = useCallback((id, select) => {
    const next = select ? selection.concat([id]) : selection.filter(stock => stock !== id)
    setChangedFiltersCallback({ ...filters, selectedStocks: next })
  }, [filters, selection, setChangedFiltersCallback])

  const selectAll = useCallback(() => {
    setChangedFiltersCallback({
      ...filters,
      selectedStocks: selection.length === stocks.length ? [] : [...stocks],
    })
  }, [filters, selection.length, setChangedFiltersCallback])

  return <ul className='filter-menu menu-sub'>
    <MenuHeader label={ t('card_stocks.title') } menuTree= { ['main'] } depth={ 1 } backCallback={ toggleViewCallback } />
    <SelectAll selectCallback={ selectAll } />
    {
      stocks.map((e) => <MenuItem
        key={ e }
        id={ e }
        label={ t(`card_stocks.${e}`) }
        selected={ !!selection.find(s => e === s) }
        callback={ toggleOption }
      />)
    }
  </ul>
}

const CardTypeMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const workingMenu = 'selectedCardTypes'

  const { t } = useTranslation()
  const [selection, setSelection] = useState<number[]>(filters[workingMenu])

  const toggleOption = useCallback((id, select) => {
    setSelection(select ? selection.concat([id]) : selection.filter(e => e !== id))
  }, [selection])

  const selectAll = useCallback(() => {
    setSelection(selection.length === cardTypes.length ? [] : cardTypes.map(e => e.id))
  }, [selection.length])

  useEffect(() => {
    if (selection === filters[workingMenu]) return

    const newFilters: Filters = {
      ...filters,
      [workingMenu]: selection,
    }

    setChangedFiltersCallback(newFilters)
  }, [filters, selection, setChangedFiltersCallback])

  return <ul className='filter-menu menu-sub'>
    <MenuHeader label={ t('cards.layouts.title') } menuTree= { ['main'] } depth={ 1 } backCallback={ toggleViewCallback } />
    <SelectAll selectCallback={ selectAll } />
    {
      cardTypes.map((e) => <MenuItem
        key={ e.id }
        id={ e.id }
        label={ t(`cards.layouts.${e.name}`) }
        selected={ !!selection.find(s => e.id === s) }
        callback={ toggleOption }
      />)
    }
  </ul>
}

const EnergyTypeMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const workingMenu = 'selectedEnergyTypes'

  const { t } = useTranslation()
  const [selection, setSelection] = useState<number[]>(filters[workingMenu])

  const toggleOption = useCallback((id, select) => {
    setSelection(select ? selection.concat([id]) : selection.filter(e => e !== id))
  }, [selection])

  const selectAll = useCallback(() => {
    setSelection(selection.length === energyTypes.length ? [] : energyTypes.map(e => e.id))
  }, [selection.length])

  useEffect(() => {
    if (selection === filters[workingMenu]) return

    const newFilters: Filters = {
      ...filters,
      [workingMenu]: selection,
    }

    setChangedFiltersCallback(newFilters)
  }, [filters, selection, setChangedFiltersCallback])

  return <ul className='filter-menu menu-sub'>
    <MenuHeader label={ t('cards.types.title') } menuTree= { ['main'] } depth={ 1 } backCallback={ toggleViewCallback } />
    <SelectAll selectCallback={ selectAll } />
    {
      energyTypes.map((e) => <MenuItem
        key={ e.id }
        id={ e.id }
        label={ t(`cards.types.${e.name}`) }
        selected={ !!selection.find(s => e.id === s) }
        callback={ toggleOption }
      />)
    }
  </ul>
}

const RarityMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const workingMenu = 'selectedRarities'

  const { t } = useTranslation()
  const [selection, setSelection] = useState<number[]>(filters[workingMenu])

  const toggleOption = useCallback((id, select) => {
    setSelection(select ? selection.concat([id]) : selection.filter(e => e !== id))
  }, [selection])

  const selectAll = useCallback(() => {
    setSelection(selection.length === rarities.length ? [] : rarities.map(e => e.id))
  }, [selection.length])

  useEffect(() => {
    if (selection === filters[workingMenu]) return

    const newFilters: Filters = {
      ...filters,
      [workingMenu]: selection,
    }

    setChangedFiltersCallback(newFilters)
  }, [filters, selection, setChangedFiltersCallback])

  return <ul className='filter-menu menu-sub'>
    <MenuHeader label={ t('cards.rarities.title') } menuTree= { ['main'] } depth={ 1 } backCallback={ toggleViewCallback } />
    <SelectAll selectCallback={ selectAll } />
    {
      rarities.map((e) => <MenuItem
        key={ e.id }
        id={ e.id }
        label={ t(`cards.rarities.${e.name}`) }
        selected={ !!selection.find(s => e.id === s) }
        callback={ toggleOption }
      />)
    }
  </ul>
}

const PriceMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const { t } = useTranslation()

  const viewTree = useMemo(() => ['main'], [])
  const viewDepth = 1

  const {
    register,
    control,
    setValue,
  } = useForm<FilterPriceRange>({
    defaultValues: {
      min: filters.priceRange.min,
      max: filters.priceRange.max,
    },
  })

  const min = useWatch({
    control,
    name: 'min',
  })

  const max = useWatch({
    control,
    name: 'max',
  })

  useEffect(() => {
    if (min === filters.priceRange.min && max === filters.priceRange.max) return

    const newFilters: Filters = {
      ...filters,
      priceRange: {
        min,
        max,
      },
    }

    setChangedFiltersCallback(newFilters)
  }, [filters, max, min, setChangedFiltersCallback])

  const goBack = useCallback(() => {
    if (min && max && Number(min) > Number(max)) {
      setValue('min', 0)
    }

    toggleViewCallback(viewTree, viewDepth - 1)
  }, [max, min, setValue, toggleViewCallback, viewTree])

  return <form className='filter-menu menu-sub'>
    <ul>
      <MenuHeader label={ t('cards.price_range.title') } menuTree= { viewTree } depth={ viewDepth } backCallback={ goBack } />
      <li className='input-menu'>
        <span>{ t('cards.price_range.min') }</span>
        <input type="number" step='1' min='0' max={ max } { ...register('min') } />
      </li>
      <li className='input-menu'>
        <span>{ t('cards.price_range.max') }</span>
        <input type="number" step='1' min={ min } { ...register('max') } />
      </li>
    </ul>
  </form>
}

const ConditionMenu = ({ filters, setChangedFiltersCallback, toggleViewCallback }: SubviewProps) => {
  const workingMenu = 'selectedConditions'

  const { t } = useTranslation()
  const [selection, setSelection] = useState<number[]>(filters[workingMenu])

  const toggleOption = useCallback((id, select) => {
    setSelection(select ? selection.concat([id]) : selection.filter(e => e !== id))
  }, [selection])

  const selectAll = useCallback(() => {
    setSelection(selection.length === conditions.length ? [] : conditions)
  }, [selection.length])

  useEffect(() => {
    if (selection === filters[workingMenu]) return

    const newFilters: Filters = {
      ...filters,
      [workingMenu]: selection,
    }

    setChangedFiltersCallback(newFilters)
  }, [filters, selection, setChangedFiltersCallback])

  return <ul className='filter-menu menu-sub'>
    <MenuHeader label={ t('cards.filter_menu.titles.rank') } menuTree= { ['main'] } depth={ 1 } backCallback={ toggleViewCallback } />
    <SelectAll selectCallback={ selectAll } />
    {
      conditions.map((e) => <MenuItem
        key={ e }
        id={ e }
        label={ t(`collections.conditions.short.${e}`) }
        selected={ selection.includes(e) }
        callback={ toggleOption }
      />)
    }
  </ul>
}

const FilterMenu = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { endpointForExistingFilters, defaultFilters, filters, setFilterMenuDisplayed, filterMenuDisplayed, searchString, isUserProfile } = useContext(SearchContext)

  const [submenuDepth, setSubmenuDepth] = useState<number>(0)
  const [submenu, setSubmenu] = useState<string[]>([])

  // these filters stay unmodified as the original state when the menu is opened
  const [initialFilters, setInitialFilters] = useState<Filters>(filters)
  const [initialSearchString, setInitialSearchString] = useState<string>(searchString)

  // these filters are modified each time the user changes a filter in the menu
  const [changedFilters, setChangedFilters] = useState<Filters>(filters)
  const [changedSearchString, setChangedSearchString] = useState<string>(searchString)

  // we can then compare the two and set the new filters
  const filtersChanged = useMemo(() => JSON.stringify([initialFilters, initialSearchString]) !== JSON.stringify([changedFilters, changedSearchString]), [changedFilters, initialFilters, initialSearchString, changedSearchString])

  const generateEndpointAfterClose = useCallback(() => {
    if (!endpointForExistingFilters) return

    if (filtersChanged) {
      // need a new endpoint!
      const currentUrl = window.location.pathname + window.location.search
      const prefix = window.location.pathname
      const search = withSortFrom(endpointForExistingFilters(changedFilters, changedSearchString) ?? '', window.location.search)
      const stay = prefix === '/' || prefix.startsWith('/categories') || ((prefix.startsWith('/u/') || prefix.startsWith('/collections/')) && !prefix.includes('/units/'))
      const path = stay ? prefix : '/categories'
      const newUrl = hrefWithSearch(path, search)

      if (newUrl === currentUrl) return

      navigate(newUrl)

      // set filters to their new states
      setInitialFilters(changedFilters)
      setInitialSearchString(changedSearchString)
    }
  }, [changedFilters, changedSearchString, endpointForExistingFilters, filtersChanged, navigate])

  const closeMenu = useCallback(() => {
    setFilterMenuDisplayed?.(false)

    generateEndpointAfterClose()
  }, [generateEndpointAfterClose, setFilterMenuDisplayed])

  const setChangedFiltersCallback = useCallback((f: Filters) => {
    if (!filterMenuDisplayed) return

    setChangedFilters(f)
  }, [filterMenuDisplayed])

  useEffect(() => {
    if (!filterMenuDisplayed) {
      setInitialFilters(filters)
      setInitialSearchString(searchString)
      setChangedFilters(filters)
      setChangedSearchString(searchString)
      return
    }

    const stocksNow = filters.selectedStocks.join()
    setChangedFilters(current => current.selectedStocks.join() === stocksNow ? current : { ...current, selectedStocks: filters.selectedStocks })
    setInitialFilters(current => current.selectedStocks.join() === stocksNow ? current : { ...current, selectedStocks: filters.selectedStocks })
  }, [filterMenuDisplayed, filters, searchString])

  const toggleView = useCallback((view: string[], depth: number) => {
    setSubmenuDepth(depth)

    setSubmenu(view)
  }, [])

  useEffect(() => {
    if (!filterMenuDisplayed) {
      // filter menu not displayed
      // if on main screen and search string didn't change, wgaf
      if (submenuDepth === 0 && initialSearchString === searchString) return

      // filter menu was closed; reset view
      setSubmenuDepth(0)
    }
  }, [filterMenuDisplayed, initialSearchString, searchString, submenuDepth])


  useEffect(() => {
    if (filterMenuDisplayed) return

    setSubmenu([])
  }, [changedFilters, filterMenuDisplayed])

  const resetFilters = useCallback(() => {
    setSubmenu([])

    setChangedFilters({
      ...defaultFilters,
      selectedView: filters.selectedView,
      selectedGame: filters.selectedGame,
      selectedStocks: isCategoryBrowse(location.pathname) ? ['for_sale'] : [],
    })

    toggleView(['main'], 0)
  }, [defaultFilters, filters.selectedGame, filters.selectedView, location.pathname, toggleView])

  const submenuUi = useMemo(() => {
    if (submenu[0] === 'game') {
      return <GameMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
    if (submenu[0] === 'view') {
      return <ViewMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
    if (submenu[0] === 'cardtype') {
      return <CardTypeMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
    if (submenu[0] === 'energytype') {
      return <EnergyTypeMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
    if (submenu[0] === 'rarity') {
      return <RarityMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
    if (submenu[0] === 'price') {
      return <PriceMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
    if (submenu[0] === 'condition') {
      return <ConditionMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
    if (submenu[0] === 'stock') {
      return <StockMenu filters={ changedFilters } setChangedFiltersCallback={ setChangedFiltersCallback } toggleViewCallback={ toggleView } submenu={ submenu } />
    }
  }, [changedFilters, setChangedFiltersCallback, submenu, toggleView])


  const listGame = useMemo(() => {
    return <li onClick={ _ => { toggleView(['game'], 1) } } className={ clsx(changedFilters.selectedGame ? 'choices-1' : 'choices-none') }>
      <span className='title'>{ t('cards.game.title') }</span>
      <span className='forward bold'>
        {
          changedFilters.selectedGame
            ? <span key={ changedFilters.selectedGame }>{ t(`cards.game.${games.find(e => e.id === changedFilters.selectedGame)?.name ?? ''}`) }</span>
            : <span>{ t('cards.filter_menu.not_selected') }</span>
        }
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.selectedGame, t, toggleView])

  const listView = useMemo(() => {
    return <li onClick={ _ => { toggleView(['view'], 1) } } className='choices-1'>
      <span className='title'>{ t('card_views.title') }</span>
      <span className='forward bold'>
        <span>{ t(`card_views.${changedFilters.selectedView}`) }</span>
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.selectedView, t, toggleView])

  const listCardType = useMemo(() => {
    return <li onClick={ _ => { toggleView(['cardtype'], 1) } } className={ clsx(changedFilters.selectedCardTypes.length > 0 ? `choices-${changedFilters.selectedCardTypes.length}` : 'choices-none') }>
      <span className='title'>{ t('cards.layouts.title') }</span>
      <span className='forward bold'>
        {
          changedFilters.selectedCardTypes.length > 0
            ? changedFilters.selectedCardTypes.length === cardTypes.length
              ? <span>{ t('cards.filter_menu.all') }</span>
              : changedFilters.selectedCardTypes.map((id, i) => {
                if (changedFilters.selectedCardTypes.length >= 6) {
                  if (i === 4) return <span className='truncate'>{ t('cards.filter_menu.and_n_more', { n: changedFilters.selectedCardTypes.length - i }) }</span>
                  if (i > 4) return null
                }

                return <span key={ id }>{ t(`cards.layouts.${cardTypes.find(e => e.id === id)?.name ?? ''}`) }</span>
              })
            : <span>{ t('cards.filter_menu.not_selected') }</span>
        }
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.selectedCardTypes, t, toggleView])

  const listEnergyType = useMemo(() => {
    return <li onClick={ _ => { toggleView(['energytype'], 1) } } className={ clsx(changedFilters.selectedEnergyTypes.length > 0 ? `choices-${changedFilters.selectedEnergyTypes.length}` : 'choices-none') }>
      <span className='title'>{ t('cards.types.title') }</span>
      <span className='forward bold'>
        {
          changedFilters.selectedEnergyTypes.length > 0
            ? changedFilters.selectedEnergyTypes.length === energyTypes.length
              ? <span>{ t('cards.filter_menu.all') }</span>
              : changedFilters.selectedEnergyTypes.map(id => {
                return <span key={ id }>{ t(`cards.types.${energyTypes.find(e => e.id === id)?.name ?? ''}`) }</span>
              })
            : <span>{ t('cards.filter_menu.not_selected') }</span>
        }
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.selectedEnergyTypes, t, toggleView])

  const listRarity = useMemo(() => {
    return <li onClick={ _ => { toggleView(['rarity'], 1) } } className={ clsx(changedFilters.selectedRarities.length > 0 ? `choices-${changedFilters.selectedRarities.length}` : 'choices-none') }>
      <span className='title'>{ t('cards.rarities.title') }</span>
      <span className='forward bold'>
        {
          changedFilters.selectedRarities.length > 0
            ? changedFilters.selectedRarities.length === rarities.length
              ? <span>{ t('cards.filter_menu.all') }</span>
              : changedFilters.selectedRarities.map((id, i) => {
                if (changedFilters.selectedRarities.length >= 10) {
                  if (i === 8) return <span className='truncate'>{ t('cards.filter_menu.and_n_more', { n: changedFilters.selectedRarities.length - i }) }</span>
                  if (i > 8) return null
                }

                return <span key={ id }>{ t(`cards.rarities.${rarities.find(e => e.id === id)?.name ?? ''}`) }</span>
              })
            : <span>{ t('cards.filter_menu.not_selected') }</span>
        }
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.selectedRarities, t, toggleView])

  const formatNumber = (n: number): string => {
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }

  const listPrice = useMemo(() => {
    const priceSet = !(!changedFilters.priceRange.min && !changedFilters.priceRange.max)

    return <li onClick={ _ => { toggleView(['price'], 1) } } className={ clsx(priceSet ? 'choices-1' : 'choices-none') }>
      <span className='title'>{ t('cards.price_range.title') }</span>
      <span className='forward bold'>
        {
          priceSet
            ? <span>{ changedFilters.priceRange.min ? formatNumber(changedFilters.priceRange.min) : 0 } ~ { changedFilters.priceRange.max ? formatNumber(changedFilters.priceRange.max) : '' }円</span>
            : <span>{ t('cards.filter_menu.not_selected') }</span>
        }
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.priceRange.max, changedFilters.priceRange.min, t, toggleView])

  const listCondition = useMemo(() => {
    return <li onClick={ _ => { toggleView(['condition'], 1) } } className={ clsx(changedFilters.selectedConditions.length > 0 ? `choices-${changedFilters.selectedConditions.length}` : 'choices-none') }>
      <span className='title'>{ t('cards.filter_menu.titles.rank') }</span>
      <span className='forward bold'>
        {
          changedFilters.selectedConditions.length > 0
            ? changedFilters.selectedConditions.length === conditions.length
              ? <span>{ t('cards.filter_menu.all') }</span>
              : changedFilters.selectedConditions.map(id => {
                return <span key={ id }>{ t(`collections.conditions.short.${id}`) }</span>
              })
            : <span>{ t('cards.filter_menu.not_selected') }</span>
        }
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.selectedConditions, t, toggleView])

  const listStock = useMemo(() => {
    if (isUserProfile) return null

    return <li onClick={ _ => { toggleView(['stock'], 1) } } className={ clsx(changedFilters.selectedStocks.length > 0 ? `choices-${changedFilters.selectedStocks.length}` : 'choices-none') }>
      <span className='title'>{ t('card_stocks.title') }</span>
      <span className='forward bold'>
        {
          changedFilters.selectedStocks.length > 0
            ? changedFilters.selectedStocks.map(id => {
              return <span key={ id }>{ t(`card_stocks.${id}`) }</span>
            })
            : <span>{ t('cards.filter_menu.not_selected') }</span>
        }
      </span>
      <span className='next'>▸</span>
    </li>
  }, [changedFilters.selectedStocks, isUserProfile, t, toggleView])

  const submenuLength = submenu.length
  const maxSubMenuDepth = Math.max(4, submenuLength)

  const compareFiltersToDefault = useCallback((next: Filters): boolean => {
    const selectedStocks = isCategoryBrowse(location.pathname) && isAvailableOnly(next.selectedStocks) ? [] : next.selectedStocks
    return JSON.stringify({ ...next, selectedStocks }) === JSON.stringify({
      ...defaultFilters,
      selectedView: next.selectedView,
    })
  }, [defaultFilters, location.pathname])

  const filtersAreDefault = useMemo(() => {
    return compareFiltersToDefault(changedFilters)
  }, [changedFilters, compareFiltersToDefault])

  const menu = useMemo(() => {
    return <div className={ clsx('filter-container', { submenuActive: submenuDepth > 1 }, `subview-show-${submenuDepth}`) }>
      <div className='filter-inner'>
        <ul className='filter-menu menu-main'>
          { listGame }
          { listView }
          { listCardType }
          { listEnergyType }
          { listRarity }
          { listPrice }
          { listCondition }
          { listStock }
        </ul>
        { submenuUi }
        { [...Array(maxSubMenuDepth - submenuLength)].map((_, i) => <ul className='filter-menu empty' key={ `empty-${i}` }></ul>) }
      </div>
      <div className='buttons'>
        <button onClick={ resetFilters } className='reset white' disabled={ filtersAreDefault }>{ t('cards.filter_menu.reset') }</button>
        <button onClick={ closeMenu }>{ t('cards.filter_menu.apply') }</button>
      </div>
    </div>
  }, [closeMenu, filtersAreDefault, listCardType, listCondition, listEnergyType, listGame, listPrice, listRarity, listStock, listView, maxSubMenuDepth, resetFilters, submenuDepth, submenuLength, submenuUi, t])

  return <>
    <div className='close-filter-menu' onClick={ closeMenu }></div>
    { menu }
  </>
}

export { FilterMenu }
